// /client/src/components/Help.js - Help page with contact form
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Navigation from './Navigation';

const Help = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Form state
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // User info for display
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  
  // FAQ accordion state
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  // FAQ data
  const faqs = [
    {
      question: "What is the ideal length of a text message?",
      answer: "Keep your messages short and conversational. The idea is sized to under 255 characters (about two or three short sentences). That's roughly the length of a friendly reminder or reflection prompt. Shorter messages are easier to read, deliver more reliably, and keep participants engaged. And they are less likely to be broken apart by network carriers."
    },
    {
      question: "What's the difference between SMS and MMS?",
      answer: "SMS (Short Message Service): plain text messages only. MMS (Multimedia Messaging Service): allows images, videos, or emojis. If you include a picture or video, the system automatically sends it as an MMS. Otherwise, it's SMS by default."
    },
    {
      question: "Why do messages show up with green bubbles instead of blue ones?",
      answer: "That's determined by the recipient's phone, not our system. Blue bubbles are Apple iMessages (sent only between iPhones). Green bubbles are regular carrier messages that work on all phones (SMS/MMS). Because our platform sends messages via mobile carriers rather than Apple's iMessage network, messages always appear with green bubbles. That's normal and expected."
    },
    {
      question: "Why do messages come from a phone number instead of my organization or program name?",
      answer: "All automated text campaigns use registered 10DLC phone numbers. Carriers don't allow business or program names to appear as the sender unless you use a special \"short code,\" which is typically reserved for very large national brands. Even though it looks like a regular phone number, that number is securely linked to your program."
    },
    {
      question: "Do iPhones and Android phones display messages differently?",
      answer: "Yes, a little. Each phone has its own style: iPhones might show a smaller photo preview or a clean text bubble. Android phones may show larger image previews or slightly different colors. The content is identical. The difference is that the visual layout changes depending on the phone type."
    },
    {
      question: "Why does this feel different from texting on my own phone?",
      answer: "You're not texting from your phone. You are using a secure, automated system designed for programs and campaigns. It handles scheduling, delivery, opt-outs, and compliance automatically. You create the content and the timing, and the system ensures it reaches everyone reliably. Think of it as your \"messaging assistant\" doing the sending so you can focus on the message itself."
    },
    {
      question: "Who can see the messages I send?",
      answer: "Only Admins and approved team members within your organization can view campaign content, delivery history, and responses. Participants see the text messages on their own phones, but they can't view or edit campaign materials."
    },
    {
      question: "Are there differences in how carriers deliver messages?",
      answer: "Sometimes, yes. Each mobile carrier (AT&T, Verizon, T-Mobile, etc.) has its own filtering rules. We follow all 10DLC compliance standards to ensure your messages are approved and delivered reliably. If a carrier flags a message, it's usually because of formatting or wording (for example, too many links or all-caps text)."
    },
    {
      question: "Why might my message be split into two or more parts?",
      answer: "If your message is longer than 255 characters, some carriers may split it into multiple texts. To avoid that, keep each message concise. Or, break longer content into two separate messages (e.g., \"Part 1\" and \"Part 2\")."
    },
    {
      question: "Can I include links, phone numbers, or emojis?",
      answer: "Yes, but use them thoughtfully and sparingly. For embedded links, use the full URLs (like https://nextmovenetwork.com) instead of shortened ones. Include phone number only if needed. Emojis are great for warmth 👍, but they can increase message size."
    },
    {
      question: "Why do I need to register my campaign?",
      answer: "Mobile carriers now require all organizations sending group or automated messages to register under the A2P 10DLC (Application-to-Person) program. This process verifies your organization, reduces spam, and protects your deliverability. Once registered, your campaigns are approved for consistent and compliant delivery."
    },
    {
      question: "Can I see how my message will look before sending?",
      answer: "Yes! Use the Preview feature in the campaign builder to see how your message appears on a typical phone screen. It's the easiest way to double-check tone, formatting, and image layout before going live."
    },
    {
      question: "Why can't I just text everyone from my own phone?",
      answer: "Personal phones can't handle automated scheduling, group delivery, opt-outs, or compliance rules. Our system ensures that messages are delivered securely, on time, and in line with carrier and privacy regulations. It's the difference between texting one friend and running a professional program."
    },
    {
      question: "What happens if someone replies \"STOP\"?",
      answer: "When someone replies STOP, they're automatically unsubscribed from your campaign. This is required by law and a network carrier policy. They won't receive future messages unless they choose to rejoin by replying \"REJOIN.\" You'll still see the opt-out recorded in your dashboard."
    },
    {
      question: "Can I send messages outside the U.S.?",
      answer: "Not yet. Currently, Navigating Next messages are delivered only to U.S. phone numbers. We'll notify all Admins if international delivery becomes available."
    },
    {
      question: "What if someone changes their phone number?",
      answer: "If a participant updates their number, simply edit their profile or re-import the new contact. Their old number will automatically stop receiving messages."
    },
    {
      question: "What if a message fails to send?",
      answer: "Occasionally, a carrier may reject a message due to filtering, connectivity, or an invalid number. You can check message delivery status in your campaign dashboard. Failed messages are clearly labeled."
    }
  ];

  // Get user details on mount
  useEffect(() => {
    if (user) {
      const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
      setUserName(fullName || 'User');
      setUserEmail(user.email || '');
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    // Validation
    if (!subject.trim()) {
      setError('Please enter a subject');
      return;
    }

    if (subject.trim().length < 3) {
      setError('Subject must be at least 3 characters long');
      return;
    }

    if (!message.trim()) {
      setError('Please enter a message');
      return;
    }

    if (message.trim().length < 10) {
      setError('Message must be at least 10 characters long');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('No authentication token found. Please log in again.');
        return;
      }

      const response = await fetch('/api/helpform', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          subject: subject.trim(),
          message: message.trim()
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccessMessage(
          data.emailSent 
            ? 'Your help request has been submitted successfully and an email notification has been sent to the support team.'
            : 'Your help request has been submitted successfully. The support team will review it shortly.'
        );
        
        // Clear form
        setSubject('');
        setMessage('');
        
        // Scroll to top to show success message
        window.scrollTo({ top: 0, behavior: 'smooth' });
        
        // Clear success message after 10 seconds
        setTimeout(() => {
          setSuccessMessage('');
        }, 10000);
      } else {
        setError(data.error || 'Failed to submit help request');
      }
    } catch (error) {
      console.error('Error submitting help request:', error);
      setError('An error occurred while submitting your request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (subject.trim() || message.trim()) {
      if (window.confirm('Are you sure you want to discard your message?')) {
        setSubject('');
        setMessage('');
        setError('');
      }
    }
  };

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  return (
    <>
      <section className="theme-light m-0 p-0" data-theme="light">
        <div className='is-flex is-flex-direction-column is-justify-content-center'>
          
          <Navigation />

          <div className='mt-2 pt-6 container px-6'>
            <div className='pb-4 is-flex is-justify-content-space-between is-align-items-center'>
              <h1 className='h1 my-2'>Help</h1>
            </div>

            {successMessage && (
              <div className="notification is-success">
                <button 
                  className="delete" 
                  onClick={() => setSuccessMessage('')}
                ></button>
                {successMessage}
              </div>
            )}

            {error && (
              <div className="notification is-danger">
                <button 
                  className="delete" 
                  onClick={() => setError('')}
                ></button>
                {error}
              </div>
            )}

            <div className="columns">
              <div className="column is-6">
                {/* Contact Form */}
                <div className="box">
                  <h2 className="title is-4 mb-4">Contact Support</h2>
                  <p className="mb-4">
                    Complete the form below and Anthony will get back to you as soon as possible. Typically within an hour. For immediate questions, call/text him at (808) 500-8501.
                  </p>

                  <form onSubmit={handleSubmit}>
                    {/* Subject field */}
                    <div className="field">
                      <label className="label">Subject *</label>
                      <div className="control">
                        <input
                          className="input"
                          type="text"
                          placeholder="Brief description of your issue or question"
                          value={subject}
                          onChange={(e) => setSubject(e.target.value)}
                          disabled={submitting}
                          maxLength={200}
                        />
                      </div>
                      <p className="ml-3 pt-1 small">
                        {subject.length}/200 characters
                      </p>
                    </div>

                    {/* Message field */}
                    <div className="field">
                      <label className="label">Message *</label>
                      <div className="control">
                        <textarea
                          className="textarea"
                          placeholder="Please provide detailed information about your request..."
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          disabled={submitting}
                          rows={8}
                          maxLength={2000}
                        />
                      </div>
                      <p className="ml-3 pt-1 small">
                        {message.length}/2000 characters
                      </p>
                    </div>

                    {/* Action buttons */}
                    <div className="pt-3 field is-grouped">
                      <div className="control">
                        <button 
                          type="submit"
                          className={`button is-primary ${submitting ? 'is-loading' : ''}`}
                          disabled={submitting || !subject.trim() || !message.trim()}
                        >
                          Submit
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>

              {/* Sidebar with FAQ accordion */}
              <div className="column is-6">
                <div className="box">
                  <h3 className="title is-5 mb-4">Frequently Asked Questions</h3>
                  
                  <style>
                    {`
                      .faq-item {
                        margin-bottom: 0.75rem;
                        border-radius: 8px;
                        overflow: hidden;
                        border: 1px solid #e0e0e0;
                        transition: all 0.3s ease;
                      }
                      
                      .faq-item:hover {
                        box-shadow: 0 2px 8px rgba(0,0,0,0.08);
                      }
                      
                      .faq-question {
                        cursor: pointer;
                        padding: 1rem 1.25rem;
                        background-color: #f5f5f5;
                        transition: all 0.3s ease;
                        user-select: none;
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                      }
                      
                      .faq-question:hover {
                        background-color: #e8e8e8;
                      }
                      
                      .faq-question.active {
                        background-color: #3273dc;
                        color: white;
                      }
                      
                      .faq-chevron {
                        transition: transform 0.3s ease;
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        min-width: 20px;
                        margin-left: 0.5rem;
                      }
                      
                      .faq-chevron i {
                        font-size: 0.75rem;
                      }
                      
                      .faq-chevron.rotated {
                        transform: rotate(180deg);
                      }
                      
                      .faq-answer-wrapper {
                        max-height: 0;
                        overflow: hidden;
                        transition: max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                        background-color: #fafafa;
                        border-top: 1px solid transparent;
                      }
                      
                      .faq-answer-wrapper.open {
                        max-height: 600px;
                        border-top: 1px solid #e0e0e0;
                      }
                      
                      .faq-answer-content {
                        padding: 1rem 1.25rem;
                        line-height: 1.6;
                      }
                    `}
                  </style>
                  
                  <div className="content">
                    {faqs.map((faq, index) => (
                      <div key={index} className="faq-item">
                        <div 
                          onClick={() => toggleFaq(index)}
                          className={`faq-question ${openFaqIndex === index ? 'active' : ''}`}
                        >
                          <span className="has-text-weight-semibold is-size-7" style={{ paddingRight: '1rem', flex: 1 }}>
                            {faq.question}
                          </span>
                          <span className={`faq-chevron ${openFaqIndex === index ? 'rotated' : ''}`}>
                            <i className="fas fa-chevron-down"></i>
                          </span>
                        </div>
                        
                        <div className={`faq-answer-wrapper ${openFaqIndex === index ? 'open' : ''}`}>
                          <div className="faq-answer-content">
                            <p className="is-size-7 mb-0">
                              {faq.answer}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>
    </>
  );
};

export default Help;